
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { AgentConfiguration, AgentModel } from "@/components/candidates/types/agent";
import { useAgentModels } from "@/hooks/useAgentModels";
import { useModelSync } from "@/hooks/useModelSync";

type ModelConfigTabProps = {
  selectedAgent: AgentConfiguration;
  updateAgentConfig: (config: Partial<AgentConfiguration>) => void;
};

export const ModelConfigTab = ({ selectedAgent, updateAgentConfig }: ModelConfigTabProps) => {
  const { data: models = [], isLoading, refetch } = useAgentModels();
  const modelSync = useModelSync();

  const handleModelChange = (modelId: string) => {
    const selectedModel = models.find(m => m.id === modelId);
    if (selectedModel) {
      updateAgentConfig({ model: selectedModel });
    }
  };

  const handleSyncModels = () => {
    modelSync.mutate({ force: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        <span>Loading models...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>AI Model Selection</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncModels}
              disabled={modelSync.isPending}
            >
              {modelSync.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Models
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Primary Model</label>
            <Select
              value={selectedAgent.model.id}
              onValueChange={handleModelChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex items-center gap-2">
                      <span>{model.name}</span>
                      <Badge variant="outline">{model.provider}</Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedAgent.model && (
            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium mb-2">Current Model Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Provider:</span> {selectedAgent.model.provider}
                </div>
                <div>
                  <span className="font-medium">Latency:</span> {selectedAgent.model.latency}
                </div>
                <div>
                  <span className="font-medium">Cost Tier:</span> {selectedAgent.model.costTier}
                </div>
                {selectedAgent.model.contextWindow && (
                  <div>
                    <span className="font-medium">Context Window:</span> {selectedAgent.model.contextWindow?.toLocaleString()} tokens
                  </div>
                )}
              </div>
              {selectedAgent.model.description && (
                <p className="text-sm text-muted-foreground mt-2">
                  {selectedAgent.model.description}
                </p>
              )}
            </div>
          )}

          {selectedAgent.recommendedModels && selectedAgent.recommendedModels.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Recommended Models</h4>
              <div className="grid gap-2">
                {selectedAgent.recommendedModels.map((model) => (
                  <Button
                    key={model.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleModelChange(model.id)}
                    className="justify-start"
                  >
                    <div className="flex items-center gap-2">
                      <span>{model.name}</span>
                      <Badge variant="secondary">{model.provider}</Badge>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
